import React from 'react';
import { get } from 'lodash';
import { fromJS } from 'immutable';

import {
    getE164NumberInRunUrlFriendlyFormat,
    getNumberInRunUrlFriendlyFormat,
    populateUrl,
} from 'flow/utils/run-url-utils';

export const VARIABLE_MISSING_VALUE = '-';
const NON_REPLACEMENT_KEYS = ['secondaryLabel'];
const URL_KEYS = ['actionUrl'];

export const buildWidgetDataFromConfig = (config, variables) => {
    config = config || {};
    variables = variables || {};

    const keys = Object.keys(config);

    const formattedConfig = Array.isArray(config) ? [] : {};
    keys.forEach((key) => {
        const value = config[key];

        if (NON_REPLACEMENT_KEYS.includes(key)) {
            formattedConfig[key] = value;
        } else if (URL_KEYS.includes(key)) {
            formattedConfig[key] = populateUrl(value, variables);
        } else if (typeof value === 'string') {
            formattedConfig[key] = formatOther(value, variables);
        } else if (React.isValidElement(value)) {
            formattedConfig[key] = value;
        } else if (typeof value === 'object' && value) {
            formattedConfig[key] = buildWidgetDataFromConfig(value, variables);
        } else {
            formattedConfig[key] = value;
        }
    });

    return formattedConfig;
};

export const formatDiscriminator = (string = '', variables = {}) => {
    const substituteDiscriminator = substituteString(true);
    getKeysFromString(string).forEach((key) => {
        const value = get(variables, key, '');
        if (key) {
            string = substituteDiscriminator(string, key, value);
        }
    });
    return string;
};

export const formatOther = (string = '', variables = {}) => {
    const substituteOther = substituteString(false);
    getKeysFromString(string).forEach((key) => {
        const value = get(variables, key, '');
        if (key) {
            string = substituteOther(string, key, value);
        }
    });
    return string;
};

export const getKeysFromString = (string = '') => {
    const braceDelimiters = string.includes('{');
    const matchResults = braceDelimiters
        ? string.matchAll(/{(.*?)}/g)
        : string.matchAll(/%(.*?)%/g);
    const keys = [];
    for (const matchResult of matchResults) {
        keys.push(matchResult[1]);
    }
    return keys;
};

export const substituteString = (isDiscriminator) => (string, key, value) => {
    const braceDelimiters = string.includes('{');
    const startDelimiter = braceDelimiters ? '{' : '%';
    const endDelimiter = braceDelimiters ? '}' : '%';
    return isDiscriminator
        ? substituteDiscriminator(
              string,
              startDelimiter,
              key,
              endDelimiter,
              value,
          )
        : substituteOther(string, startDelimiter, key, endDelimiter, value);
};

const substituteDiscriminator = (
    string,
    startDelimiter,
    key,
    endDelimiter,
    value,
) =>
    value
        ? string.replace(`${startDelimiter}${key}${endDelimiter}`, value)
        : VARIABLE_MISSING_VALUE;

const substituteOther = (string, startDelimiter, key, endDelimiter, value) =>
    string
        .replace(`${startDelimiter}${key}${endDelimiter}`, value)
        .replace(/\s+/g, ' ');

export const getHistoricCallVariables = (
    callRecord,
    ownCountryCode,
    ownNumber,
) => {
    if (!callRecord) return {};

    const variables = {};

    const contact = callRecord.get('contact');
    const direction = callRecord.get('direction');
    const isIncoming = direction === 'incoming';
    const recordNumber = callRecord.getIn(['number', 'number']);

    const caller = isIncoming ? recordNumber : ownNumber;
    const callee = isIncoming ? ownNumber : recordNumber;

    variables['CONTACT'] = contact ? parseMetadata(contact).toJS() : contact;

    variables['NUMBER'] = getNumberInRunUrlFriendlyFormat(
        caller,
        ownCountryCode,
    );
    const intNumber = getE164NumberInRunUrlFriendlyFormat(
        caller,
        ownCountryCode,
    );
    variables['INT-NUMBER'] = intNumber;
    variables['caller'] = intNumber;

    variables['B-NUMBER'] = getNumberInRunUrlFriendlyFormat(
        callee,
        ownCountryCode,
    );
    variables['callee'] = getE164NumberInRunUrlFriendlyFormat(
        callee,
        ownCountryCode,
    );
    variables['counterpart'] = getE164NumberInRunUrlFriendlyFormat(
        isIncoming ? caller : callee,
        ownCountryCode,
    );

    return variables;
};

export const discriminatorIsPresent = (discriminator, variables) => {
    if (!discriminator) {
        // If the discriminator is undefined, we want to show the widget anyway
        return true;
    }
    const formattedDiscriminator = formatDiscriminator(
        discriminator,
        variables,
    );
    return formattedDiscriminator !== VARIABLE_MISSING_VALUE;
};

export const chooseConfig = (discriminator, variables, present, missing) => {
    return discriminatorIsPresent(discriminator, variables) ? present : missing;
};

export const parseMetadata = (contact) => {
    if (!contact || typeof contact.get('metadata') !== 'string') {
        return contact;
    }

    try {
        return contact.set(
            'metadata',
            fromJS(JSON.parse(contact.get('metadata'))),
        );
    } catch (_e) {
        //do nothing
    }

    return contact;
};

export const isURL = (url) => {
    try {
        new URL(url);
        return true;
    } catch (_error) {
        return false;
    }
};
